import { Injectable, inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { MessageService } from '@openng/optimus-ui/api';

export interface Toast {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message?: string;
  duration?: number;
  persistent?: boolean;
  action?: {
    label: string;
    handler: () => void;
  };
}

@Injectable({
  providedIn: 'root'
})
export class ToastService {
  private toastsSubject = new BehaviorSubject<Toast[]>([]);
  public toasts$ = this.toastsSubject.asObservable();

  private readonly messageService = inject(MessageService);

  private generateId(): string {
    return Math.random().toString(36).substr(2, 9);
  }

  private addToast(toast: Omit<Toast, 'id'>): string {
    const id = this.generateId();
    const newToast: Toast = {
      ...toast,
      id,
      duration: toast.duration || 5000
    };

    // Use PrimeNG MessageService
    this.messageService.add({
      severity: toast.type === 'error' ? 'error' : toast.type === 'warning' ? 'warn' : toast.type === 'success' ? 'success' : 'info',
      summary: toast.title,
      detail: toast.message,
      life: toast.persistent ? 0 : newToast.duration
    });

    const currentToasts = this.toastsSubject.value;
    this.toastsSubject.next([...currentToasts, newToast]);

    return id;
  }

  success(title: string, message?: string, options?: Partial<Toast>): string {
    return this.addToast({
      type: 'success',
      title,
      message,
      ...options
    });
  }

  error(title: string, message?: string, options?: Partial<Toast>): string {
    return this.addToast({
      type: 'error',
      title,
      message,
      persistent: true, // Errors should be persistent by default
      ...options
    });
  }

  warning(title: string, message?: string, options?: Partial<Toast>): string {
    return this.addToast({
      type: 'warning',
      title,
      message,
      ...options
    });
  }

  info(title: string, message?: string, options?: Partial<Toast>): string {
    return this.addToast({
      type: 'info',
      title,
      message,
      ...options
    });
  }

  removeToast(id: string): void {
    const currentToasts = this.toastsSubject.value;
    this.toastsSubject.next(currentToasts.filter(toast => toast.id !== id));
  }

  clearAll(): void {
    this.toastsSubject.next([]);
  }

  // Convenience methods
  saveSuccess(entityName: string = 'Item'): string {
    return this.success(`${entityName} saved`, 'Your changes have been saved successfully.');
  }

  deleteSuccess(entityName: string = 'Item'): string {
    return this.success(`${entityName} deleted`, 'The item has been removed.');
  }

  saveError(entityName: string = 'Item', error?: string): string {
    return this.error(
      `Failed to save ${entityName.toLowerCase()}`,
      error || 'Please try again or contact support if the problem persists.'
    );
  }

  deleteError(entityName: string = 'Item', error?: string): string {
    return this.error(
      `Failed to delete ${entityName.toLowerCase()}`,
      error || 'Please try again or contact support if the problem persists.'
    );
  }

  networkError(): string {
    return this.error(
      'Network error',
      'Please check your internet connection and try again.'
    );
  }

  validationError(message: string): string {
    return this.warning('Validation error', message);
  }
}